import type { BlokProps } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { Asset } from "@/components/ui/Asset";
import { Button } from "@/components/ui/Button";
import { Typography } from "@/components/ui/Typography";
import type { SectionHighlightItemStoryblok } from "@/generated/sb/sb-types";
import { Link } from "@/i18n/routing";
import { sbLink } from "@/lib/storyblok/link";
import { Reveal } from "../ui/Reveal";

export const SectionHighlightItemBlok = ({
	blok,
	className,
}: BlokProps<SectionHighlightItemStoryblok> & {
	className?: string;
}) => {
	const hasContent = Boolean(blok.caption || blok.title || blok.description || blok.ctaLabel);
	const href = sbLink(blok.ctaLink)?.href;

	return (
		<BlokWrap editable={blok} asChild>
			<div className={clsx("relative w-full overflow-hidden bg-background-bg-dark", className)}>
				{blok.asset && (
					<Asset
						asset={blok.asset}
						sizes="(min-width: 1024px) 50vw, 100vw"
						className="object-cover"
					/>
				)}
				<div className="absolute inset-0 bg-overlay-3" aria-hidden />
				{hasContent && (
					<div className="absolute inset-0 flex flex-col items-center justify-center gap-grid-component-spacing-md px-grid-section-horizontal-margin-page-base text-center text-text-sand-text-2">
						{blok.caption && (
							<Reveal animation="fade-bottom" delay={100}>
								<Typography variant="label-sm-reg" as="p" className="leading-[1.15]">
									{blok.caption}
								</Typography>
							</Reveal>
						)}
						{blok.title && (
							<Reveal animation="fade-bottom" delay={200}>
								<Typography variant="heading-display-md" as="h3" className="max-w-[840px]">
									{blok.title}
								</Typography>
							</Reveal>
						)}
						{blok.description && (
							<Reveal animation="fade-bottom" delay={300}>
								<Typography variant="body-sm-reg" as="p" className="max-w-[512px] leading-[1.5]">
									{blok.description}
								</Typography>
							</Reveal>
						)}
						{blok.ctaLabel && (
							<Reveal animation="fade-bottom" delay={300}>
								{href ? (
									<Button as={Link} href={href} variant="solid" color="white" size="large">
										{blok.ctaLabel}
									</Button>
								) : (
									<Button variant="solid" color="white" size="large">
										{blok.ctaLabel}
									</Button>
								)}
							</Reveal>
						)}
					</div>
				)}
			</div>
		</BlokWrap>
	);
};
